<template>
  <base-modal
    show
    size="md"
    :close-on-escape="!loadingCheckout"
    :close-on-click-outside="!loadingCheckout"
    :has-close-btn="!globalModals.length && !loadingCheckout"
    :modal-id="MODAL_ID"
    :heading="heading"
    :cancel-button="false"
    @close:modal="closeModal"
  >
    <template v-if="formStep === PURCHASE_STEPS.CART" #default>
      <ugc-purchase-mod
        usd
        :mod="mod"
        :token-pack="tokenPack"
        :game-name="game.name"
        :token-name="tokenName"
      />

      <base-loading-icon
        v-if="cartLoading || loadingGamePacks"
        show
        class="tw-relative tw-max-h-24 tw-overflow-hidden"
      />

      <div>
        <div :id="TILIA_WIDGET" class="tw-flex tw-flex-col tw-size-full" />
      </div>

      <p
        v-if="isMonetizationWebSelfRefundsDisabled(game.monetization_options)"
        class="tw-mt-4"
      >
        <i18n-t keypath="marketplace.ugc_not_refundable">
          <template #ugc_plural>
            {{ ugcName(game, true, false).value }}
          </template>
          <template #terms>
            <a
              class="mod-link"
              href="https://mod.io/terms"
              @click.prevent="openTermsOfUseModal()"
              >{{ $t('terms') }}
            </a>
          </template>
          <template #refund>
            <a
              class="mod-link"
              href="https://mod.io/refund"
              @click.prevent="openRefundPolicyModal()"
              >{{ $t('refund') }}</a
            >
          </template>
          <template #ugc>
            {{ ugcName(game, true, true).value }}
          </template>
        </i18n-t>
      </p>
    </template>
    <template v-else-if="formStep === PURCHASE_STEPS.CHECKOUT" #default>
      <ugc-purchase-mod
        usd
        :mod="mod"
        :tax="transactionTax"
        :token-pack="tokenPack"
        :game-name="game.name"
        :token-name="tokenName"
      />

      <ugc-purchase-wallet
        usd
        :step="PURCHASE_STEPS.CHECKOUT"
        :token-name="tokenName"
        :payment-methods="paymentMethods"
        :price="mod.price"
      />
    </template>
    <template v-else-if="formStep === PURCHASE_STEPS.RECEIPT" #default>
      <ugc-purchase-mod
        usd
        :mod="mod"
        :tax="transactionTax"
        :step="formStep"
        :game-name="game.name"
        :token-name="tokenName"
        :show-header-and-price="false"
      />

      <p class="tw-mb-6">
        <i18n-t keypath="marketplace.ugc_purchase_success">
          <template #ugc>
            {{ ugcName(game, true, true).value }}
          </template>
          <template #library>
            <base-link :to="{ name: LIBRARY_ROUTE }">{{
              $t('library')
            }}</base-link>
          </template>
        </i18n-t>
      </p>

      <base-divider class="tw-mt-6 tw-mb-4" />

      <p
        class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-widest tw-mb-3"
      >
        {{ $t('summary') }}
      </p>
      <table class="tw-w-full">
        <tbody>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">{{ $t('date') }}</td>
            <td class="tw-text-right">{{ dataCheckout.purchase_date }}</td>
          </tr>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">
              {{ $t('marketplace.transaction_id') }}
            </td>
            <td class="tw-text-right">{{ dataCheckout.transaction_id }}</td>
          </tr>
          <tr></tr>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">
              {{ $t('marketplace.payment_method') }}
            </td>
            <td class="tw-text-right">
              {{ paymentMethods.map((m) => m.display).join(', ') }}
            </td>
          </tr>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">
              {{ $t('marketplace.total_paid') }}
            </td>
            <td class="tw-text-right">
              {{
                humanPriceFormat(
                  convertToDecimal(dataCheckout.gross_amount + transactionTax)
                )
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <template v-if="formStep === PURCHASE_STEPS.CHECKOUT" #footer-top>
      <base-checkbox
        test-id="checkout-terms"
        class="tw--mt-3 tw-mb-3"
        large
        :error-gap="false"
        :background="false"
        :errors="errors.monTermsAgreed"
        :model-value="monTermsAgreed"
        @input="(input) => updateAgreed(input)"
      >
        <template #label>
          <span class="tw-text-theme">
            <i18n-t keypath="marketplace.agreement">
              <template #terms>
                <a
                  class="mod-link"
                  href="https://mod.io/terms"
                  @click.prevent="openTermsOfUseModal()"
                  >{{ $t('terms') }}
                </a>
              </template>
              <template #refund>
                <a
                  class="mod-link"
                  href="https://mod.io/refund"
                  @click.prevent="openRefundPolicyModal()"
                  >{{ $t('refund') }}</a
                >
              </template>
            </i18n-t>
          </span>
        </template>
      </base-checkbox>
    </template>

    <template
      v-if="
        [PURCHASE_STEPS.CHECKOUT, PURCHASE_STEPS.RECEIPT].includes(formStep)
      "
      #footer
    >
      <base-button
        v-if="formStep === PURCHASE_STEPS.CHECKOUT"
        test-id="checkout-button"
        secondary
        :disabled="isLoading"
        class="tw-w-full sm:tw-w-auto tw-min-w-[10.5rem]"
        @click="returnToPaymentMethods"
      >
        {{ $t('marketplace.change_payment') }}
      </base-button>
      <base-button
        test-id="cart-btn"
        :primary="formStep !== PURCHASE_STEPS.RECEIPT"
        :secondary="formStep === PURCHASE_STEPS.RECEIPT"
        class="tw-w-full sm:tw-w-auto tw-min-w-[10.5rem]"
        :disabled="isLoading"
        :status="status"
        @click="button.onClick"
      >
        {{ button.text }}
      </base-button>
    </template>
  </base-modal>

  <teleport to="body">
    <cancel-transaction-modal
      v-if="isCancelModalOpen"
      @confirm="closeModal(true)"
      @close:modal="hideCancelModal()"
    />
  </teleport>
</template>

<script>
import UgcPurchaseWallet from '@components/Monetization/Components/UgcPurchaseWallet.vue'
import CancelTransactionModal from '@shared/components/Modals/CancelTransactionModal.vue'
import UgcPurchaseMod from '@components/Monetization/Components/UgcPurchaseMod.vue'
import { isMonetizationWebSelfRefundsDisabled } from '@helpers/bitHelper.js'
import { setGlobalModal, globalModals } from '@composables/useModal.js'
import { onMounted, computed, nextTick, toRefs, ref } from 'vue'
import { initTiliaWidget } from '@shared/js/plugins/tilia.js'
import { MODAL_AGREEMENT } from '@config/globalModals.js'
import { humanDateTimeFormat } from '@helpers/utils.js'
import { monTermsConfig } from '@config/validation.js'
import { LIBRARY_ROUTE } from '@config/routeNames.js'
import { TILIA_WIDGET } from '@config/htmlIDs.js'
import { useI18n } from 'vue-i18n'
import {
  getOrCreateBuyerGameWalletRequest,
  getAccountPaymentMethodsRequest,
  getGameTokenPacksRequest,
  subscribeToModRequest,
  checkoutModRequest,
} from '@services'
import {
  CHECKOUT_UNIFIED_INTENT,
  CHECKOUT_UNIFIED_COMMIT,
  TILIA_TYPE_PAYMENT,
  TILIA_STATE_CANCEL,
  TILIA_STATE_ERROR,
} from '@config/monetization.js'
import {
  useResourceValue,
  useMonetization,
  useValidate,
  useStatus,
  useModal,
  useAsync,
  addToast,
} from '@composables'
import {
  subscriptionStore,
  monetizationStore,
  purchasesStore,
  tokenPackStore,
} from '@stores'
import {
  AGREEMENT_TYPE_REFUND,
  AGREEMENT_TYPE_TOU,
  PURCHASE_STEPS,
} from '@config/options.js'
import {
  RESOURCE_ALREADY_SUBSCRIBED,
  MOD_ALREADY_OWNED,
} from '@shared/js/config/errors.js'
import {
  convertToDecimal,
  humanPriceFormat,
} from '@helpers/monetizationUtils.js'

const MODAL_ID = 'purchase-modal'

export default {
  components: {
    CancelTransactionModal,
    UgcPurchaseWallet,
    UgcPurchaseMod,
  },
  props: {
    mod: {
      type: Object,
      required: true,
    },
    game: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close:purchasemodal', 'purchase:complete'],
  setup(props, { emit }) {
    const { game, mod } = toRefs(props)

    const formStep = ref(PURCHASE_STEPS.CART)
    const monTermsAgreed = ref(false)
    const paymentMethods = ref(null)
    const transactionTax = ref(0)
    const intentTransactionId = ref(null)

    const { ugcName, tokenName: getTokenName } = useResourceValue()
    const tokenName = getTokenName()
    const { t } = useI18n()
    const { validate, errors, hasErrors } = useValidate(
      monTermsConfig.validation
    )
    const {
      isModalOpen: isCancelModalOpen,
      showModal: showCancelModal,
      hideModal: hideCancelModal,
    } = useModal()

    const {
      loadingRegister,
      loadingSession,
      setGameWallet,
      getSession,
      unsetNonce,
      nonce,
    } = useMonetization()

    const { tokenPacks, setTokenPacks } = tokenPackStore(game.value.name_id)
    const { addSubscription, removeSubscription } = subscriptionStore()
    const { transactionKey, setTransactionKey } = monetizationStore()
    const { addPurchase } = purchasesStore()

    const {
      loading: loadingGamePacks,
      error: errorGamePacks,
      data: dataGamePacks,
      run: runGamePacks,
    } = useAsync(
      () => getGameTokenPacksRequest(game.value.name_id),
      t('marketplace.error_sku_packs', { ugc: ugcName(game, true, true).value })
    )

    const {
      loading: loadingBalance,
      error: errorBalance,
      data: dataBalance,
      run: runBalance,
    } = useAsync(
      getOrCreateBuyerGameWalletRequest,
      t('marketplace.error_wallet')
    )

    const {
      error: paymentMethodsError,
      data: paymentMethodsData,
      run: runPaymentMethods,
    } = useAsync(
      getAccountPaymentMethodsRequest,
      t('marketplace.error_payment_methods')
    )

    // Checkout process
    const {
      errorRef: errorRefCheckout,
      loading: loadingCheckout,
      error: errorCheckout,
      data: dataCheckout,
      run: runCheckout,
    } = useAsync(
      (data) => checkoutModRequest(game.value.id, mod.value.id, data),
      t('marketplace.error_complete')
    )

    const {
      errorRef: subscribeErrorRef,
      error: subscribeError,
      run: subscribeToMod,
    } = useAsync(
      () =>
        subscribeToModRequest({
          gameId: game.value.id,
          modId: mod.value.id,
        }),
      t('subscribe_fail')
    )

    const cartLoading = computed(
      () => loadingRegister.value || loadingSession.value
    )

    const isLoading = computed(
      () =>
        loadingCheckout.value || loadingBalance.value || loadingGamePacks.value
    )

    const skuId = computed(
      () => mod.value.skus.find((sku) => sku.portal === 'web')?.id || null
    )
    const tokenPack = computed(
      () => tokenPacks.value?.find((pack) => pack.id === skuId.value) || null
    )
    const modTransactionKey = computed(
      () => transactionKey.value[`${game.value.id}-mod-${mod.value.id}`]
    )

    const { updateStatus, statusType, status } = useStatus({
      loading: isLoading,
      error: errorCheckout,
    })

    const heading = computed(() => {
      switch (formStep.value) {
        case PURCHASE_STEPS.CHECKOUT:
          return t('marketplace.checkout')
        case PURCHASE_STEPS.RECEIPT:
          return t('marketplace.ugc_purchase_confirmed', {
            ugc: ugcName(game, true).value,
          })
        default:
          return t('marketplace.order_summary')
      }
    })

    const button = computed(() => {
      switch (formStep.value) {
        case PURCHASE_STEPS.CHECKOUT:
          return {
            text: t('marketplace.confirm_purchase'),
            onClick: confirmPurchase,
          }
        case PURCHASE_STEPS.RECEIPT:
          return {
            text: t('marketplace.continue_browsing'),
            onClick: closeModal,
          }
        default:
          return {}
      }
    })

    onMounted(async () => {
      fetchGamePacks()
      if (!modTransactionKey.value) initTransactionKey()

      const sessionHasError = await getSession()

      if (sessionHasError) closeModal(true)
      else initTilia()
    })

    function initTilia() {
      initTiliaWidget({
        flow: TILIA_TYPE_PAYMENT,
        redirect: nonce.value,
        callback: onTokenComplete,
        transactionAmount: mod.value.price,
      })
    }

    function initTransactionKey() {
      setTransactionKey(
        game.value.id,
        `mod-${mod.value.id}`,
        Date.now().toString(36)
      )
    }

    function closeModal(confirm = false) {
      if (confirm || formStep.value !== PURCHASE_STEPS.CHECKOUT) {
        emit('close:purchasemodal')
        setTransactionKey(game.value.id, `mod-${mod.value.id}`, null)
        unsetNonce()
        return
      }

      showCancelModal()
    }

    function updateAgreed(input) {
      monTermsAgreed.value = input

      validate('monTermsAgreed', monTermsAgreed.value)
    }

    function openRefundPolicyModal() {
      setGlobalModal({
        modal: MODAL_AGREEMENT,
        data: { agreementType: AGREEMENT_TYPE_REFUND, noOpacity: true },
      })
    }

    function openTermsOfUseModal() {
      setGlobalModal({
        modal: MODAL_AGREEMENT,
        data: { agreementType: AGREEMENT_TYPE_TOU, noOpacity: true },
      })
    }

    function updateStep(step) {
      formStep.value = step
    }

    async function fetchGamePacks() {
      if (!tokenPacks.value) {
        await runGamePacks()

        if (!errorGamePacks.value) {
          setTokenPacks(
            dataGamePacks.value.data.toSorted((a, b) => a.amount - b.amount)
          )
        }
      }
    }

    async function getWalletBalance() {
      await runBalance()

      if (!errorBalance.value) {
        setGameWallet(game.value.id, dataBalance.value)
      } else {
        closeModal(true)
      }
    }

    async function purchaseIntent() {
      const data = {
        payment_method_id: paymentMethods.value[0].payment_method_id,
        type: CHECKOUT_UNIFIED_INTENT,
        refund_accepted: true,
        terms_accepted: true,
        idempotent_key: modTransactionKey.value,
      }

      await runCheckout(data)

      if (!errorCheckout.value) {
        intentTransactionId.value = dataCheckout.value.transaction_id
        transactionTax.value = dataCheckout.value.tax
        updateStep(PURCHASE_STEPS.CHECKOUT)
      } else if (errorRefCheckout.value === MOD_ALREADY_OWNED) {
        addToast({
          title: t('marketplace.error_already_purchased', {
            ugc: ugcName(game, true).value,
          }),
          isSuccess: true,
        })
        addPurchase(mod.value.id, mod.value)
        updateStatus(statusType.SUCCESS)
        subscribe()
        closeModal(true)
      } else {
        paymentMethods.value = null
        nextTick(initTilia)
      }
    }

    async function purchaseCommit() {
      const data = {
        transaction_id: intentTransactionId.value,
        type: CHECKOUT_UNIFIED_COMMIT,
        idempotent_key: modTransactionKey.value,
      }
      await runCheckout(data)
      if (!errorCheckout.value) {
        emit('purchase:complete')
        dataCheckout.value.purchase_date = humanDateTimeFormat(
          dataCheckout.value.purchase_date
        )
        addPurchase(mod.value.id, mod.value)
        subscribe()
        updateStep(PURCHASE_STEPS.RECEIPT)
        updateStatus(statusType.SUCCESS)
        getWalletBalance()
        setTransactionKey(game.value.id, `mod-${mod.value.id}`, null)
      } else if (errorRefCheckout.value === MOD_ALREADY_OWNED) {
        addToast({
          title: t('marketplace.error_already_purchased', {
            ugc: ugcName(game, true).value,
          }),
          isSuccess: true,
        })
        addPurchase(mod.value.id, mod.value)
        updateStatus(statusType.SUCCESS)
        subscribe()
        closeModal(true)
      }
    }

    function confirmPurchase() {
      validate('monTermsAgreed', monTermsAgreed.value)
      if (hasErrors()) {
        updateStatus(statusType.ERROR)
        return
      }
      purchaseCommit()
    }

    async function subscribe() {
      addSubscription(mod.value.id, mod.value)

      await subscribeToMod()

      if (
        subscribeError.value &&
        subscribeErrorRef.value !== RESOURCE_ALREADY_SUBSCRIBED
      ) {
        removeSubscription(mod.value.id)
      }
    }

    async function initCheckout(result) {
      await runPaymentMethods()

      if (!paymentMethodsError.value) {
        paymentMethods.value = result.paymentMethods.map((m) => {
          const matchingMethod =
            paymentMethodsData.value.data?.[m.payment_method_id]
          if (matchingMethod) {
            m.display = matchingMethod.display_string
            m.provider = matchingMethod.method_class
          }
          return m
        })
        purchaseIntent()
      } else {
        initTilia()
      }
    }

    function onTokenComplete(result) {
      if ([TILIA_STATE_CANCEL, TILIA_STATE_ERROR].includes(result.state)) {
        closeModal(true)
      } else {
        result.paymentMethods.sort((a, b) => b.amount - a.amount)
        initCheckout(result)
      }
    }

    function returnToPaymentMethods() {
      initTransactionKey()
      intentTransactionId.value = null
      transactionTax.value = 0
      updateStep(PURCHASE_STEPS.CART)
      nextTick(initTilia)
    }

    return {
      isMonetizationWebSelfRefundsDisabled,
      returnToPaymentMethods,
      openRefundPolicyModal,
      openTermsOfUseModal,
      isCancelModalOpen,
      loadingGamePacks,
      convertToDecimal,
      humanPriceFormat,
      hideCancelModal,
      loadingCheckout,
      transactionTax,
      PURCHASE_STEPS,
      paymentMethods,
      monTermsAgreed,
      LIBRARY_ROUTE,
      globalModals,
      TILIA_WIDGET,
      dataCheckout,
      updateAgreed,
      cartLoading,
      closeModal,
      isLoading,
      tokenName,
      tokenPack,
      MODAL_ID,
      formStep,
      heading,
      ugcName,
      button,
      errors,
      status,
    }
  },
}
</script>
